import { Set } from "immutable";
import { has } from "lodash";

import { keyArrayFromMap } from "../../../utils/immutableExtensions";
import dateFunctions, { isDateValid } from "../../definition/calendar/dateFunctions";
import getIndividualItemDates from "./getIndividualItemDates";

const areDateValuesSame = (newItem, prevItem) =>
  newItem &&
  prevItem &&
  newItem.startDate === prevItem.startDate &&
  newItem.duration === prevItem.duration &&
  newItem.preferredStartDate === prevItem.preferredStartDate &&
  newItem.preferredDuration === prevItem.preferredDuration &&
  newItem.latestStartDate === prevItem.latestStartDate &&
  newItem.durationToEarliestEnd === prevItem.durationToEarliestEnd &&
  newItem.ongoing === prevItem.ongoing;

const itemIsBeyondNow = (item, calendar, nowTs) =>
  isDateValid(item.preferredStartDate)
    ? item.ongoing
      ? item.preferredStartDate.timestamp > nowTs
      : dateFunctions.dateByAddingSpan(calendar, item.preferredStartDate, item.preferredDuration).timestamp > nowTs
    : false;

const getDescendantValues = (item, calendar, todayDate, newItemsById, newDescendantsById, mutatingBeyondNowResult) => {
  const descendantIds = newDescendantsById.get(item.id);
  const hasOngoingDescendants = Boolean(descendantIds.find(dId => newItemsById.get(dId).ongoing));
  let hasDescendantBeyondNow = false;
  if (item.ongoing) {
    hasDescendantBeyondNow = Boolean(
      descendantIds.find(dId => {
        if (!has(mutatingBeyondNowResult, dId)) {
          mutatingBeyondNowResult[dId] = itemIsBeyondNow(newItemsById.get(dId), calendar, todayDate.timestamp);
        }
        return mutatingBeyondNowResult[dId];
      })
    );
  }
  return { hasOngoingDescendants, hasDescendantBeyondNow };
};

// Changes: everything in ItemDatesRecord
// will probably eventually consider splitting that out into its own object?
const updateItemDisplayDates = (
  state,
  action,
  newAndChangedIdSet,
  todayDate,
  newItemsById,
  prevItemsById,
  prevAncestorsById,
  newAncestorsById,
  newDescendantsById,
  prevDescendantsById,
  newCalendar,
  prevCalendar
) => {
  const calendarChanged = newCalendar !== prevCalendar;

  // We are only interested in changes to an item's date, or changes to a descendant's ongoing/date
  // which are all inside ItemRecords.
  // So if there are no changed ItemRecords, we do not need to calculate anything.
  if (!calendarChanged && newAndChangedIdSet.size === 0) {
    return state;
  }

  // If we reach here, we may need to update some date stuff
  // Work out which items are possibly relevant
  let affectedIds = null;

  if (calendarChanged) {
    affectedIds = keyArrayFromMap(state);
  } else {
    // Work out which items have potentially changed dates, which are:
    // any items with new date values
    // any items with changes to descendants
    const dateChangedIds = newAndChangedIdSet.filter(
      cId =>
        !areDateValuesSame(newItemsById.get(cId), prevItemsById.get(cId)) ||
        newDescendantsById.get(cId) !== prevDescendantsById.get(cId)
    );

    // Aside from those items, we will also need to look at their ancestors,
    // since they may have a change to hasOngoingDescendants
    const ancestorIds = dateChangedIds.reduce((result, cId) => {
      result.push(...newAncestorsById.get(cId).toArray());
      return result;
    }, []);
    affectedIds = Set([...dateChangedIds.toArray(), ...ancestorIds]);
  }

  // If there are no IDs to check, let's bail
  if (affectedIds.size === 0) {
    return state;
  }

  // We keep track of which items are "beyond now" so that we don't need to repeat the test
  // unnecessarily
  let mutatingBeyondNowResult = {};

  // Now, for every testId we will call getIndividualItemDates
  return state.withMutations(s => {
    affectedIds.forEach(cId => {
      const item = newItemsById.get(cId);
      const { hasOngoingDescendants, hasDescendantBeyondNow } = getDescendantValues(
        item,
        newCalendar,
        todayDate,
        newItemsById,
        newDescendantsById,
        mutatingBeyondNowResult
      );
      const itemDatesRecord = getIndividualItemDates(
        item,
        newCalendar,
        todayDate,
        hasOngoingDescendants,
        hasDescendantBeyondNow
      );
      s.update(cId, d => d.merge(itemDatesRecord.toObject()));
    });
  });
};

export default updateItemDisplayDates;
